import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { NavBar } from 'components/NavBar/NavBar'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { tokens } from 'styling/tokens'
import { PageWrapper, DashboardCard } from 'styling/GlobalStyles'
import { Head1 } from 'styling/Typography'
import {
  HeaderRow,
  HeaderActions,
  KpiGrid,
  DashGrid,
  fleetColors,
  CompositionGrid,
  CategoryTile,
  TileIcon,
  TileCount,
  TileName,
} from './FleetOverview.styled'
import { machineryIcon } from 'assets/icons/MachineryIcon'
import { WidgetHeader, WidgetTitle, WidgetDetailsLink } from 'pages/HrDashboard/Widgets/widgetParts.styled'
import {
  WidgetBody,
  BarRow,
  BarLabel,
  BarTrack,
  BarFill,
  BarValue,
  ListRow,
  ListText,
  ListTitle,
} from 'pages/HrDashboard/hrLayout.styled'
import { AlertListRow } from 'components/ui/AlertListRow/AlertListRow'
import {
  getFleetDashboardOverview,
  getFleetCategories,
  getFleetCategoryUtilization,
} from 'features/machinery/fleetMachinesSlice'

export const FleetOverview: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()

  const dashboardOverview = useSelector((state: RootState) => state.fleetMachines.dashboardOverview)
  const categories = useSelector((state: RootState) => state.fleetMachines.categories)
  const categoryUtilization = useSelector((state: RootState) => state.fleetMachines.categoryUtilization)

  useEffect(() => {
    dispatch(getFleetDashboardOverview())
    dispatch(getFleetCategories())
    dispatch(getFleetCategoryUtilization())
  }, [])

  const kpis = [
    {
      label: 'Availability',
      value: dashboardOverview ? `${dashboardOverview.availabilityPercentage}%` : '—',
      sub: `${dashboardOverview?.operationalMachineCount ?? '—'} of ${dashboardOverview?.totalMachineCount ?? '—'} operational`,
    },
    {
      label: 'Avg Utilization (24h)',
      value: dashboardOverview ? `${dashboardOverview.utilizationPercentage}%` : '—',
      sub: 'vs target 70%',
    },
    {
      label: 'In Use Now',
      value: String(dashboardOverview?.inUseCount ?? '—'),
      sub: `${dashboardOverview?.operationalMachineCount ?? '—'} operational`,
    },
    {
      label: 'Under Maintenance',
      value: String(dashboardOverview?.inMaintenanceCount ?? '—'),
      sub: 'in the workshop',
    },
    {
      label: 'Broken/Critical',
      value: String(dashboardOverview?.brokenCount ?? '—'),
      sub: 'needs attention',
    },
    {
      label: 'Low Fuel',
      value: String(dashboardOverview?.lowFuelPercentageCount ?? '—'),
      sub: 'machines below 25%',
    },
  ]

  return (
    <>
      <NavBar />
      <PageWrapper noFooter>
        <HeaderRow>
          <Head1 style={{ fontSize: '1.5rem', lineHeight: 1.3 }} color={tokens.text.primary}>
            Fleet Overview
          </Head1>
          <HeaderActions />
        </HeaderRow>

        <KpiGrid style={{ gridTemplateColumns: 'repeat(6, 1fr)' }}>
          {kpis.map((k) => (
            <MetricCard
              key={k.label}
              label={k.label}
              value={k.value}
              sublabel={k.sub || undefined}
            />
          ))}
        </KpiGrid>

        <DashGrid>
          <DashboardCard height='18rem'>
            <WidgetHeader>
              <WidgetTitle>Fleet composition</WidgetTitle>
              <WidgetDetailsLink onClick={() => navigate('/machinery/fleet')}>View all ›</WidgetDetailsLink>
            </WidgetHeader>
            <WidgetBody>
              <CompositionGrid>
                {categories.map((c) => (
                  <CategoryTile key={c.id}>
                    <TileIcon>{machineryIcon}</TileIcon>
                    <TileName title={c.name}>{c.name}</TileName>
                    <TileCount>{c.machineCount}</TileCount>
                  </CategoryTile>
                ))}
              </CompositionGrid>
            </WidgetBody>
          </DashboardCard>

          <DashboardCard height='18rem'>
            <WidgetHeader>
              <WidgetTitle>Cost breakdown · last 7d</WidgetTitle>
            </WidgetHeader>
            <WidgetBody>
              {[
                { label: 'Fuel', amount: '$84.2k', pct: 42 },
                { label: 'Maintenance & Parts', amount: '$51.8k', pct: 26 },
                { label: 'Operator Labor', amount: '$39.6k', pct: 20 },
                { label: 'Depreciation', amount: '$24.6k', pct: 12 },
              ].map((c) => (
                <ListRow key={c.label}>
                  <ListText>
                    <ListTitle>{c.label}</ListTitle>
                  </ListText>
                  <span style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span style={{ color: tokens.text.primary, fontWeight: 600, fontSize: tokens.font.body }}>{c.amount}</span>
                    <span style={{ color: tokens.text.muted, fontSize: tokens.font.label }}>{c.pct}%</span>
                  </span>
                </ListRow>
              ))}
              <ListRow style={{ borderBottom: 'none', borderTop: `1px solid ${tokens.border.hairline}`, paddingTop: '0.75rem' }}>
                <ListText>
                  <ListTitle style={{ color: tokens.text.muted }}>Total fleet cost</ListTitle>
                </ListText>
                <span style={{ color: tokens.accent.base, fontWeight: 700, fontSize: tokens.font.h2 }}>$200.0k</span>
              </ListRow>
            </WidgetBody>
          </DashboardCard>
        </DashGrid>

        <DashGrid>
          <DashboardCard height='18rem'>
            <WidgetHeader>
              <WidgetTitle>Utilization by category · vs 70% target</WidgetTitle>
            </WidgetHeader>
            <WidgetBody>
              {categoryUtilization.map((u) => {
                const pct = u.utilizationPercentage ?? 0
                return (
                  <BarRow key={u.id}>
                    <BarLabel>{u.name}</BarLabel>
                    <BarTrack>
                      <BarFill $pct={pct} $color={pct >= 70 ? fleetColors.success : tokens.accent.base} />
                    </BarTrack>
                    <BarValue>{pct}%</BarValue>
                  </BarRow>
                )
              })}
            </WidgetBody>
          </DashboardCard>

          <DashboardCard height='18rem'>
            <WidgetHeader>
              <WidgetTitle>Active alerts</WidgetTitle>
            </WidgetHeader>
            <WidgetBody>
              {[
                { title: 'Low fuel — CAT 793F #12', detail: '18% remaining · North Pit', status: 'warning' as const, tag: 'Fuel' },
                { title: 'Overdue PM — Komatsu PC5500', detail: 'Scheduled 2 days ago · Maintenance Bay', status: 'danger' as const, tag: 'Overdue' },
                { title: 'Idle threshold — Liebherr T282', detail: '4.2h idle · Haulage Loop B', status: 'info' as const, tag: 'Idle' },
              ].map((alert) => (
                <AlertListRow
                  key={alert.title}
                  title={alert.title}
                  meta={alert.detail}
                  pillLabel={alert.tag}
                  pillTone={alert.status}
                />
              ))}
            </WidgetBody>
          </DashboardCard>
        </DashGrid>
      </PageWrapper>
    </>
  )
}
